import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from './queryKeys'

export const useOrgFavouritesApi = ({ orgId }: { orgId?: string | null }) => {
  const supabase = useSupabaseClient()

  const { data: productIds = [], isLoading } = useQuery({
    queryKey: [queryKeys.orgFavourites, orgId],
    queryFn: async () => {
      if (!orgId) return []

      const { data, error } = await supabase
        .from('organization_favourite')
        .select('product_id')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data ?? []).map((row) => row.product_id).filter(Boolean)
    },
    enabled: !!orgId,
  })

  return {
    productIds,
    isLoading,
  }
}

export type OrgFavouritesCategoryGroup = {
  categoryId: number
  categoryTitle: string | null
  productIds: number[]
}

const categorySortLabel = (title: string | null) => {
  const raw = title ?? ''
  const primary = raw.includes(',')
    ? raw.split(',')[0]
    : raw.split(' || ')[0]
  return primary?.trim() ?? ''
}

export const useOrgFavouritesByCategoryApi = ({
  orgId,
}: {
  orgId?: string | null
}) => {
  const supabase = useSupabaseClient()

  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.orgFavourites, orgId, 'byCategory'],
    queryFn: async (): Promise<OrgFavouritesCategoryGroup[]> => {
      if (!orgId) return []

      const { data: favRows, error } = await supabase
        .from('organization_favourite')
        .select('product_id')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })

      if (error) throw error

      const productIds = (favRows ?? [])
        .map((row) => row.product_id)
        .filter((id): id is number => id != null)

      if (productIds.length === 0) return []

      const { data: cpRows, error: cpError } = await supabase
        .from('category_product')
        .select('product_id, category (id, title, parent_category_id)')
        .in('product_id', productIds)

      if (cpError) throw cpError

      type CatRow = {
        id: number
        title: string | null
        parent_category_id: number | null
      }

      const productToCategories = new Map<number, CatRow[]>()

      for (const row of cpRows ?? []) {
        const pid = row.product_id
        const cat = row.category as CatRow | CatRow[] | null
        const resolved = Array.isArray(cat) ? cat[0] : cat
        if (pid == null || !resolved) continue
        const list = productToCategories.get(pid) ?? []
        list.push(resolved)
        productToCategories.set(pid, list)
      }

      const pickCategoryForProduct = (cats: CatRow[]): CatRow | null => {
        if (cats.length === 0) return null
        const subs = cats.filter((c) => c.parent_category_id != null)
        const pool = subs.length > 0 ? subs : cats
        return pool.reduce((a, b) => (a.id < b.id ? a : b))
      }

      const productToCategory = new Map<number, CatRow>()
      for (const [pid, cats] of productToCategories) {
        const chosen = pickCategoryForProduct(cats)
        if (chosen) productToCategory.set(pid, chosen)
      }

      const byCategoryId = new Map<
        number,
        { title: string | null; productIds: number[] }
      >()
      const uncategorized: number[] = []

      for (const pid of productIds) {
        const cat = productToCategory.get(pid)
        if (!cat) {
          uncategorized.push(pid)
          continue
        }
        let entry = byCategoryId.get(cat.id)
        if (!entry) {
          entry = { title: cat.title, productIds: [] }
          byCategoryId.set(cat.id, entry)
        }
        entry.productIds.push(pid)
      }

      const groups: OrgFavouritesCategoryGroup[] = Array.from(
        byCategoryId.entries()
      ).map(([categoryId, v]) => ({
        categoryId,
        categoryTitle: v.title,
        productIds: v.productIds,
      }))

      groups.sort((a, b) =>
        categorySortLabel(a.categoryTitle).localeCompare(
          categorySortLabel(b.categoryTitle),
          undefined,
          { sensitivity: 'base' }
        )
      )

      if (uncategorized.length > 0) {
        groups.push({
          categoryId: -1,
          categoryTitle: null,
          productIds: uncategorized,
        })
      }

      return groups
    },
    enabled: !!orgId,
  })

  return {
    categoryGroups: data ?? [],
    isLoading,
  }
}

export const useOrgFavouriteCheckApi = ({
  orgId,
  productIds,
}: {
  orgId?: string | null
  productIds?: number[]
}) => {
  const supabase = useSupabaseClient()

  const { data: favouritedIds = new Set<number>() } = useQuery({
    queryKey: [queryKeys.orgFavourites, 'check', orgId, productIds],
    queryFn: async () => {
      if (!orgId || !productIds || productIds.length === 0) {
        return new Set<number>()
      }

      const { data, error } = await supabase
        .from('organization_favourite')
        .select('product_id')
        .eq('organization_id', orgId)
        .in('product_id', productIds)

      if (error) throw error

      const set = new Set<number>()
      data?.forEach((row) => {
        if (row.product_id != null) set.add(row.product_id)
      })
      return set
    },
    enabled: !!orgId && !!productIds && productIds.length > 0,
  })

  return {
    favouritedIds,
  }
}

export const useToggleOrgFavouriteApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const toggleFavourite = useMutation({
    mutationFn: async ({
      orgId,
      productId,
      isFavourited,
    }: {
      orgId: string
      productId: number
      isFavourited: boolean
    }) => {
      if (isFavourited) {
        const { error } = await supabase
          .from('organization_favourite')
          .delete()
          .eq('organization_id', orgId)
          .eq('product_id', productId)

        if (error) throw error
        return { productId, isFavourited: false }
      } else {
        const { error } = await supabase
          .from('organization_favourite')
          .insert({
            organization_id: orgId,
            product_id: productId,
          })

        if (error) throw error
        return { productId, isFavourited: true }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.orgFavourites] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.products] })
    },
  })

  return {
    toggleFavourite,
  }
}

export class AddOrgFavouriteByArticleError extends Error {
  constructor(
    public readonly kind: 'not_found' | 'ambiguous' | 'duplicate'
  ) {
    super(kind)
    this.name = 'AddOrgFavouriteByArticleError'
  }
}

export const useAddOrgFavouriteByArticleApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const addByArticle = useMutation({
    mutationFn: async ({
      orgId,
      articleNumber,
    }: {
      orgId: string
      articleNumber: string
    }) => {
      const trimmed = articleNumber.trim()
      if (!trimmed) {
        throw new Error('Article number is required')
      }

      const { data: rows, error: findError } = await supabase
        .from('product_open')
        .select('id')
        .eq('article_number', trimmed)

      if (findError) throw findError

      if (!rows?.length) {
        throw new AddOrgFavouriteByArticleError('not_found')
      }
      if (rows.length > 1) {
        throw new AddOrgFavouriteByArticleError('ambiguous')
      }

      const productId = rows[0].id
      if (productId == null) {
        throw new AddOrgFavouriteByArticleError('not_found')
      }

      const { error: insertError } = await supabase
        .from('organization_favourite')
        .insert({
          organization_id: orgId,
          product_id: productId,
        })

      if (insertError) {
        if (insertError.code === '23505') {
          throw new AddOrgFavouriteByArticleError('duplicate')
        }
        throw insertError
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.orgFavourites] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.products] })
    },
  })

  return { addByArticle }
}
